/**
 * Pure helpers for the PLP grid's pagination math. Extracted from
 * `shop.$categoryId.tsx` so the regression paths (Load More page
 * calculation, counter offset) are unit-testable without a React
 * fixture.
 *
 * Vocabulary:
 *   - `gridStartPage` — 1-indexed page number of the FIRST tile in the
 *     accumulated grid. `1` when the user landed on page 1 / no `?page`
 *     param OR after Start From The Beginning prepended pages 1..N-1.
 *     `N>1` when the user landed deep on `?page=N` and hasn't run Start
 *     Over yet.
 *   - `pagesInGrid` — how many full pages-worth of products are stacked
 *     into the grid right now. Derived from `hits.length / itemsPerPage`
 *     with ceiling rounding so dedupe (CIO can return duplicates across
 *     paginated calls) doesn't undercount.
 */

export function computeNextLoadMorePage({
  hitsCount,
  gridStartPage,
  itemsPerPage,
  pageCap,
}: {
  hitsCount: number;
  gridStartPage: number;
  itemsPerPage: number;
  /** Server PAGE_CAP. Pages beyond it are unreachable (server clamps). */
  pageCap: number;
}): number | null {
  const pagesInGrid = Math.max(1, Math.ceil(hitsCount / itemsPerPage));
  const lastLoadedPage = gridStartPage + pagesInGrid - 1;
  const next = lastLoadedPage + 1;
  // Never emit `?page=N>cap`: the server clamps it to the cap, so the fetch
  // returns duplicate hits that dedupe drops — a paid-for no-op (and before
  // the server clamp existed, a full View All fan-out). Signal "nothing left".
  return next > pageCap ? null : next;
}

export function computeOffsetStart({
  gridStartPage,
  itemsPerPage,
  viewAll,
}: {
  gridStartPage: number;
  itemsPerPage: number;
  viewAll: boolean;
}): number {
  if (viewAll) return 1;
  return (gridStartPage - 1) * itemsPerPage + 1;
}
